import React, { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '../services/api';
import { 
  Users, Plus, Edit, Trash2, Calendar, Clock, 
  UserCheck, UserX, Briefcase, DollarSign,
  Shield, CheckCircle, XCircle
} from 'lucide-react';

const HR = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [roles, setRoles] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', identity_number: '', email: '', phone: '',
    address: '', department: '', position: '', salary: 0, start_date: '', status: 'active'
  });

  const [leaveData, setLeaveData] = useState({
    employee_id: '', leave_type: 'annual', start_date: '', end_date: '', total_days: 0, reason: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'employees') {
        const [employeesRes, statsRes] = await Promise.all([
          hrAPI.getEmployees(),
          hrAPI.getEmployeeStats()
        ]);
        setEmployees(employeesRes.data);
        setStats(statsRes.data);
      } else if (activeTab === 'leave') {
        const res = await hrAPI.getLeaveRequests({});
        setLeaveRequests(res.data);
      } else if (activeTab === 'attendance') {
        const [todayRes, statsRes] = await Promise.all([
          hrAPI.getTodayAttendance(),
          hrAPI.getAttendanceStats()
        ]);
        setAttendance(todayRes.data);
        setStats(statsRes.data);
      } else if (activeTab === 'roles') {
        const [rolesRes, settingsRes] = await Promise.all([
          hrAPI.getRoles(),
          hrAPI.getSettings()
        ]);
        setRoles(rolesRes.data);
        const settingsObj = {};
        settingsRes.data.forEach(s => { settingsObj[s.setting_key] = s.setting_value; });
        setSettings(settingsObj);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await hrAPI.updateEmployee(editingItem.id, formData);
      } else {
        await hrAPI.createEmployee(formData);
      }
      setShowModal(false);
      setEditingItem(null);
      setFormData({ first_name: '', last_name: '', identity_number: '', email: '', phone: '', address: '', department: '', position: '', salary: 0, start_date: '', status: 'active' });
      fetchData();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await hrAPI.createLeaveRequest(leaveData);
      setShowLeaveModal(false);
      setLeaveData({ employee_id: '', leave_type: 'annual', start_date: '', end_date: '', total_days: 0, reason: '' });
      fetchData();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
    }
  };

  const handleLeaveApproval = async (id, status) => {
    try {
      await hrAPI.updateLeaveRequest(id, { status, approved_by: 1 });
      fetchData();
    } catch (error) {
      console.error('Güncelleme hatası:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu personeli silmek istediğinize emin misiniz?')) {
      try {
        await hrAPI.deleteEmployee(id);
        fetchData();
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  const handleSettingsSave = async () => {
    try {
      await hrAPI.updateSettings(settings);
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      console.error('Kaydetme hatası:', error);
    }
  };

  const tabs = [
    { id: 'employees', label: 'Personel', icon: Users },
    { id: 'leave', label: 'İzinler', icon: Calendar },
    { id: 'attendance', label: 'Devam', icon: Clock },
    { id: 'roles', label: 'Yetkiler', icon: Shield },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">İnsan Kaynakları</h1>
        {activeTab === 'employees' && (
          <button
            onClick={() => { setEditingItem(null); setShowModal(true); }}
            data-help-target="hr-employee-create"
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus size={18} />
            Yeni Personel
          </button>
        )}
        {activeTab === 'leave' && (
          <button
            onClick={() => setShowLeaveModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={18} />
            İzin Talebi
          </button>
        )}
      </div>

      {/* Summary Cards */}
      {activeTab === 'employees' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-full"><Users className="text-blue-600" size={24} /></div>
              <div>
                <p className="text-sm text-gray-500">Aktif Personel</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-full"><Briefcase className="text-purple-600" size={24} /></div>
              <div>
                <p className="text-sm text-gray-500">Departmanlar</p>
                <p className="text-2xl font-bold">{stats.departments?.length || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-full"><DollarSign className="text-green-600" size={24} /></div>
              <div>
                <p className="text-sm text-gray-500">Toplam Maaş</p>
                <p className="text-2xl font-bold">₺{stats.total_salary?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-full"><UserCheck className="text-green-600" size={24} /></div>
              <div><p className="text-sm text-gray-500">Bugün GelEN</p><p className="text-2xl font-bold">{stats.today_present || 0}</p></div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-full"><UserX className="text-red-600" size={24} /></div>
              <div><p className="text-sm text-gray-500">Bugün Gelenmeyen</p><p className="text-2xl font-bold">{stats.today_absent || 0}</p></div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-full"><Clock className="text-yellow-600" size={24} /></div>
              <div><p className="text-sm text-gray-500">Geç kalan</p><p className="text-2xl font-bold">{stats.today_late || 0}</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-10">Yükleniyor...</div>
      ) : (
        <>
          {activeTab === 'employees' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Ad Soyad</th>
                    <th className="px-4 py-3 text-left">Departman</th>
                    <th className="px-4 py-3 text-left">Pozisyon</th>
                    <th className="px-4 py-3 text-left">Telefon</th>
                    <th className="px-4 py-3 text-left">Başlangıç</th>
                    <th className="px-4 py-3 text-center">Durum</th>
                    <th className="px-4 py-3 text-center">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3 font-medium">{item.first_name} {item.last_name}</td>
                      <td className="px-4 py-3">{item.department || '-'}</td>
                      <td className="px-4 py-3">{item.position || '-'}</td>
                      <td className="px-4 py-3">{item.phone || '-'}</td>
                      <td className="px-4 py-3">{item.start_date || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.status === 'active' ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 mr-2"><Edit size={18} /></button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">Personel bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'leave' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Personel</th>
                    <th className="px-4 py-3 text-left">İzin Türü</th>
                    <th className="px-4 py-3 text-left">Başlangıç</th>
                    <th className="px-4 py-3 text-left">Bitiş</th>
                    <th className="px-4 py-3 text-center">Gün</th>
                    <th className="px-4 py-3 text-center">Durum</th>
                    <th className="px-4 py-3 text-center">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">{item.first_name} {item.last_name}</td>
                      <td className="px-4 py-3">{item.leave_type === 'annual' ? 'Yıllık İzin' : item.leave_type === 'sick' ? 'Sağlık İzni' : 'Diğer'}</td>
                      <td className="px-4 py-3">{item.start_date}</td>
                      <td className="px-4 py-3">{item.end_date}</td>
                      <td className="px-4 py-3 text-center">{item.total_days}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${item.status === 'approved' ? 'bg-green-100 text-green-800' : item.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {item.status === 'approved' ? 'Onaylı' : item.status === 'rejected' ? 'Reddedildi' : 'Bekliyor'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.status === 'pending' && (
                          <>
                            <button onClick={() => handleLeaveApproval(item.id, 'approved')} className="text-green-600 hover:text-green-800 mr-2"><CheckCircle size={18} /></button>
                            <button onClick={() => handleLeaveApproval(item.id, 'rejected')} className="text-red-600 hover:text-red-800"><XCircle size={18} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {leaveRequests.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">İzin talebi bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Personel</th>
                    <th className="px-4 py-3 text-left">Departman</th>
                    <th className="px-4 py-3 text-left">Giriş</th>
                    <th className="px-4 py-3 text-left">Çıkış</th>
                    <th className="px-4 py-3 text-center">Gecikme (dk)</th>
                    <th className="px-4 py-3 text-center">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">{item.first_name} {item.last_name}</td>
                      <td className="px-4 py-3">{item.department}</td>
                      <td className="px-4 py-3">{item.check_in || '-'}</td>
                      <td className="px-4 py-3">{item.check_out || '-'}</td>
                      <td className="px-4 py-3 text-center">{item.late_minutes > 0 ? <span className="text-red-600 font-bold">{item.late_minutes}</span> : '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${item.status === 'present' ? 'bg-green-100 text-green-800' : item.status === 'absent' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {item.status === 'present' ? 'Geldi' : item.status === 'absent' ? 'Gelmedi' : 'İzinli'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {attendance.length === 0 && (
                    <tr><td colSpan="6" className="text-center py-8 text-gray-500">Bugün devam kaydı bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-bold mb-4">Roller ve Yetkiler</h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left">Rol Adı</th>
                        <th className="px-4 py-3 text-left">Açıklama</th>
                        <th className="px-4 py-3 text-left">İzinler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-4 py-3 font-medium">{item.name}</td>
                          <td className="px-4 py-3">{item.description || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {item.permissions?.map((p, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">{p}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Sistem Ayarları</h3>
                <div className="bg-white rounded-lg p-4 shadow">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Şirket Adı</label>
                      <input type="text" value={settings.company_name || ''} onChange={e => setSettings({...settings, company_name: e.target.value})}
                        className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Para Birimi</label>
                      <select value={settings.currency || 'TL'} onChange={e => setSettings({...settings, currency: e.target.value})}
                        className="w-full border rounded px-3 py-2">
                        <option value="TL">TL</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">KDV Oranı (%)</label>
                      <input type="number" value={settings.tax_rate || 18} onChange={e => setSettings({...settings, tax_rate: e.target.value})}
                        className="w-full border rounded px-3 py-2" />
                    </div>
                    <button onClick={handleSettingsSave} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                      Ayarları Kaydet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Personel Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">{editingItem ? 'Personel Düzenle' : 'Yeni Personel'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Ad *</label><input type="text" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Soyad *</label><input type="text" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">TC Kimlik No</label><input type="text" value={formData.identity_number} onChange={e => setFormData({...formData, identity_number: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Telefon</label><input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">E-posta</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Departman</label><input type="text" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Pozisyon</label><input type="text" value={formData.position} onChange={e => setFormData({...formData, position: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Maaş</label><input type="number" value={formData.salary} onChange={e => setFormData({...formData, salary: parseFloat(e.target.value)})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Başlangıç Tarihi</label><input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium mb-1">Durum</label><select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full border rounded px-3 py-2"><option value="active">Aktif</option><option value="inactive">Pasif</option></select></div>
              </div>
              <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded">İptal</button><button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Kaydet</button></div>
            </form>
          </div>
        </div>
      )}

      {/* İzin Talebi Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">İzin Talebi</h2>
            <form onSubmit={handleLeaveSubmit}>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">Personel *</label><select required value={leaveData.employee_id} onChange={e => setLeaveData({...leaveData, employee_id: e.target.value})} className="w-full border rounded px-3 py-2"><option value="">Seçin</option>{employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">İzin Türü *</label><select required value={leaveData.leave_type} onChange={e => setLeaveData({...leaveData, leave_type: e.target.value})} className="w-full border rounded px-3 py-2"><option value="annual">Yıllık İzin</option><option value="sick">Sağlık İzni</option><option value="other">Diğer</option></select></div>
                <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium mb-1">Başlangıç *</label><input type="date" required value={leaveData.start_date} onChange={e => setLeaveData({...leaveData, start_date: e.target.value})} className="w-full border rounded px-3 py-2" /></div><div><label className="block text-sm font-medium mb-1">Bitiş *</label><input type="date" required value={leaveData.end_date} onChange={e => setLeaveData({...leaveData, end_date: e.target.value})} className="w-full border rounded px-3 py-2" /></div></div>
                <div><label className="block text-sm font-medium mb-1">Açıklama</label><textarea value={leaveData.reason} onChange={e => setLeaveData({...leaveData, reason: e.target.value})} className="w-full border rounded px-3 py-2" rows="2" /></div>
              </div>
              <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={() => setShowLeaveModal(false)} className="px-4 py-2 border rounded">İptal</button><button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Gönder</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HR;
